import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { EndpointBuilder } from '@reduxjs/toolkit/query/endpointDefinitions';
import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/fetchBaseQuery';
import type { 
  LoginData, 
  RegisterData, 
  AuthResponse 
} from '../../types/auth';
import type { ApiResponse } from '../../types/api';
import { logger } from '../../utils/logger';

// Auth API endpoints
export const authApiEndpoints = (builder: EndpointBuilder<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>, never, 'api'>) => ({
  login: builder.mutation<AuthResponse['token'], LoginData>({
    query: (credentials) => {
      logger.debug('[AuthAPI] Login attempt with:', credentials.email);
      return {
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      };
    },
    transformResponse: (response: { data: AuthResponse }) => {
      logger.debug('[AuthAPI] Login response:', response);
      const token = response.data.token;
      // Token'ı localStorage'a kaydet
      if (token) {
        logger.debug('[AuthAPI] Saving token to localStorage');
        localStorage.setItem('token', token);
      }
      return token;
    },
    transformErrorResponse: (response: { status: number, data: any }) => {
      logger.error('[AuthAPI] Login error:', response);
      return {
        status: response.status,
        data: {
          message: response.data?.message || response.data?.error || 'Login failed'
        }
      };
    },
  }),
  
  register: builder.mutation<AuthResponse, RegisterData>({
    query: (data) => ({
      url: '/auth/register',
      method: 'POST',
      body: data,
    }),
    transformResponse: (response: ApiResponse<AuthResponse>) => {
      logger.debug('[AuthAPI] Register response:', response);
      if (response.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        return response.data;
      }
      throw new Error('Registration failed: No token received');
    },
    transformErrorResponse: (response: { status: number, data: any }) => {
      logger.error('[AuthAPI] Register error:', response);
      return {
        status: response.status,
        data: {
          message: response.data?.message || response.data?.error || 'Registration failed'
        }
      };
    },
  }),
  
  forgotPassword: builder.mutation<{ message: string }, { email: string }>({
    query: (data) => ({
      url: '/auth/forgot-password',
      method: 'POST',
      body: data,
    }),
  }),
  
  resetPassword: builder.mutation<
    { message: string },
    { token: string; new_password: string }
  >({
    query: (data) => ({
      url: '/auth/reset-password',
      method: 'POST',
      body: data,
    }),
  }),
  
  verifyEmail: builder.mutation<{ message: string }, { token: string }>({
    query: (data) => ({
      url: '/auth/verify-email',
      method: 'POST',
      body: data,
    }),
  }),
  
  resendVerificationEmail: builder.mutation<{ message: string }, { email: string }>({
    query: (data) => ({
      url: '/auth/resend-verification',
      method: 'POST',
      body: data
    }),
    transformResponse: (response: ApiResponse<{ message: string }>) => response.data,
  }),
}); 